/**
 * `useRecord(ref)` (13-23): one register record and its sibling rows, for the record page (13-25) and its panels.
 *
 *   - A live ref (`INC-42`) is read through `recordService.getRecord`: the item with every field-set column, then its
 *     `ParentRef` sibling rows, oldest first. A practice ref (`INC-P3`, D-13-23-1) is read from the practice records
 *     store, so a record lodged in practice opens in practice. Anything else is `unknown-ref`.
 *   - Exposes `{ status, row, siblings, preparedByUpn, error, refreshing, reload }`. `preparedByUpn` is the latest
 *     `Status → Evaluating` row's `ActorUPN` (D-13-L-19), the fact 13-21's approve-and-close and 13-22's gate read.
 *   - Refetches on window focus, at most once a minute (D-13-09-3). No retries of its own: a failed read shows its
 *     error, and `reload()` is the person's to press. Owed audit rows are retried through the Writer
 *     (`retryRecordAudit`), never from here.
 *   - Loading is derived (the latest result is not for the latest request), never set inside the effect (13-08,
 *     ISS-185).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useActionRuntime } from './useActionRuntime';
import { getRecord, preparedByUpn as preparerOf } from '../services/recordService';
import type { SiblingRow } from '../services/recordService';
import { readPracticeRecord } from '../services/commitAdapter';
import { errorStatus } from '../services/graphClient';
import { isLiveRef, isPracticeRef } from '../lib/recordQuery';
import type { RecordRow } from '../types/records';

/** The focus refetch floor (D-13-09-3). */
export const RECORD_REFETCH_MIN_MS = 60_000;

export type RecordStatus = 'loading' | 'ready' | 'not-found' | 'unknown-ref' | 'error';

export interface UseRecordResult {
  status: RecordStatus;
  row: RecordRow | null;
  siblings: SiblingRow[];
  preparedByUpn: string | undefined;
  error: string | null;
  /** A reload is running while an earlier result is shown. */
  refreshing: boolean;
  reload: () => void;
}

interface Loaded {
  key: string;
  status: Exclude<RecordStatus, 'loading'>;
  row: RecordRow | null;
  siblings: SiblingRow[];
  preparedByUpn: string | undefined;
  error: string | null;
}

export function useRecord(ref: string | null | undefined): UseRecordResult {
  const { io, siteError } = useActionRuntime();
  const [trigger, setTrigger] = useState(0);
  const lastLoad = useRef(0);

  const kind = !ref ? 'none' : isPracticeRef(ref) ? 'practice' : isLiveRef(ref) ? 'live' : 'bad';
  // One key per request: a new one whenever the ref, the site or the trigger changes.
  const key = `${ref ?? ''}|${io?.siteId ?? ''}|${trigger}`;
  const ready = kind === 'practice' || kind === 'bad' || (kind === 'live' && io !== null);

  const [loaded, setLoaded] = useState<Loaded | null>(null);

  useEffect(() => {
    if (!ready || !ref) return;
    let cancelled = false;
    lastLoad.current = Date.now();
    const land = (l: Omit<Loaded, 'key'>) => {
      if (!cancelled) setLoaded({ key, ...l });
    };
    const read = async (): Promise<Omit<Loaded, 'key'>> => {
      if (kind === 'bad') {
        return { status: 'unknown-ref', row: null, siblings: [], preparedByUpn: undefined, error: `Not a register reference: ${ref}` };
      }
      if (kind === 'practice') {
        const rec = readPracticeRecord(ref);
        return rec
          ? { status: 'ready', row: rec.row, siblings: rec.siblings, preparedByUpn: preparerOf(rec.siblings), error: null }
          : { status: 'not-found', row: null, siblings: [], preparedByUpn: undefined, error: null };
      }
      try {
        const r = await getRecord(io as NonNullable<typeof io>, ref);
        return { status: 'ready', row: r.row, siblings: r.siblings, preparedByUpn: r.preparedByUpn, error: null };
      } catch (err) {
        if (errorStatus(err) === 404) return { status: 'not-found', row: null, siblings: [], preparedByUpn: undefined, error: null };
        return {
          status: 'error',
          row: null,
          siblings: [],
          preparedByUpn: undefined,
          error: err instanceof Error ? err.message : 'The record could not be read.',
        };
      }
    };
    void read().then(land);
    return () => {
      cancelled = true;
    };
  }, [key, ready, ref, kind, io]);

  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    const onFocus = () => {
      if (Date.now() - lastLoad.current >= RECORD_REFETCH_MIN_MS) reload();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [reload]);

  return useMemo<UseRecordResult>(() => {
    const current = loaded && loaded.key === key ? loaded : null;
    // A reload keeps the last result for the same ref on screen.
    const shown = current ?? (loaded && loaded.key.startsWith(`${ref ?? ''}|`) ? loaded : null);
    if (!ref) {
      return { status: 'unknown-ref', row: null, siblings: [], preparedByUpn: undefined, error: null, refreshing: false, reload };
    }
    if (!shown) {
      const error = kind === 'live' && !io && siteError ? siteError : null;
      return { status: error ? 'error' : 'loading', row: null, siblings: [], preparedByUpn: undefined, error, refreshing: false, reload };
    }
    return {
      status: shown.status,
      row: shown.row,
      siblings: shown.siblings,
      preparedByUpn: shown.preparedByUpn,
      error: shown.error,
      refreshing: current === null,
      reload,
    };
  }, [loaded, key, ref, kind, io, siteError, reload]);
}
